import React, { createContext, useContext, useEffect, useState } from "react";
import { ThemeModeComponent } from "../../../assets/ts/layout";
import { toAbsoluteUrl } from "../../../helpers";
import { useStyleContext } from "../../../helpers/StyleContext";
import { getAuth } from "../../../../app/modules/auth";
import { themeModelSKey, themeMenuModeLSKey } from "./themeModeKeys";

export type ThemeModeType = "dark" | "light" | "system" | "custom";
export { themeModelSKey, themeMenuModeLSKey };

const systemMode = ThemeModeComponent.getSystemMode() as
  | "light"
  | "dark"
  | "custom";

type ThemeModeContextType = {
  mode: ThemeModeType;
  menuMode: ThemeModeType;
  updateMode: (_mode: ThemeModeType) => void;
  updateMenuMode: (_mode: ThemeModeType) => void;
};

const themeModeSwitchHelper = (_mode: ThemeModeType) => {
  // change background image url
  const mode = _mode !== "system" ? _mode : systemMode;
  const imageUrl =
    "/media/patterns/header-bg" + (mode === "light" ? ".jpg" : "-dark.png");
  document.body.style.backgroundImage = `url("${toAbsoluteUrl(imageUrl)}")`;
};

const getThemeModeFromLocalStorage = (lsKey: string): ThemeModeType => {
  if (!localStorage) {
    return "light";
  }

  const data = localStorage.getItem(lsKey);
  if (
    data === "dark" ||
    data === "light" ||
    data === "system" ||
    data === "custom"
  ) {
    return data;
  }

  if (document.documentElement.hasAttribute("data-bs-theme")) {
    const dataTheme = document.documentElement.getAttribute("data-bs-theme");
    if (
      dataTheme &&
      (dataTheme === "dark" ||
        dataTheme === "light" ||
        dataTheme === "system" ||
        dataTheme === "custom")
    ) {
      return dataTheme;
    }
  }

  return "system";
};

const defaultThemeMode: ThemeModeContextType = {
  mode: getThemeModeFromLocalStorage(themeModelSKey),
  menuMode: getThemeModeFromLocalStorage(themeMenuModeLSKey),
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
};

const ThemeModeContext = createContext<ThemeModeContextType>({
  mode: defaultThemeMode.mode,
  menuMode: defaultThemeMode.menuMode,
  updateMode: (_mode: ThemeModeType) => {},
  updateMenuMode: (_menuMode: ThemeModeType) => {},
});

const useThemeMode = () => useContext(ThemeModeContext);

const ThemeModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [mode, setMode] = useState<ThemeModeType>(defaultThemeMode.mode);
  const [menuMode, setMenuMode] = useState<ThemeModeType>(
    defaultThemeMode.menuMode
  );
  const { updateStyle, getStyleColors } = useStyleContext();
  const apiToken = getAuth();
  const DEFAULT_STYLE = {
    type: "",
    primary_color: "",
    secondary_color: "",
    primary_text_color: "",
    secondary_text_color: "",
    sidebar_background_color: "",
    sidebar_text_color: "",
    header_background_color: "",
    header_text_color: "",
    footer_background_color: "",
    footer_text_color: "",
    toolbar_background_color: "",
    toolbar_text_color: "",
  };

  const updateMode = (
    _mode: ThemeModeType,
    saveInLocalStorage: boolean = true
  ) => {
    let styleColors = DEFAULT_STYLE;
    if (apiToken) {
      getStyleColors()
        .then((data) => {
          styleColors = data;
          if (_mode === "custom") {
            document.documentElement.setAttribute("data-bs-theme", "light");
            updateStyle(styleColors);
          } else {
            document.body.removeAttribute("style");
            updateStyle(DEFAULT_STYLE);
            updateStyle({ type: styleColors.type });
          }
          document.body.setAttribute("style", "overflow-x:hidden");
        })
        .catch((error) => {
          console.error(
            error?.response?.data?.error?.statusCode,
            error?.response?.data?.error?.message
          );
        });
    }
    setMode(_mode);

    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeModelSKey, _mode);
    }

    if (saveInLocalStorage && _mode !== "custom") {
      const updatedMode = _mode === "system" ? systemMode : _mode;
      document.documentElement.setAttribute("data-bs-theme", updatedMode);
    }
    ThemeModeComponent.init();
  };

  const updateMenuMode = (
    _menuMode: ThemeModeType,
    saveInLocalStorage: boolean = true
  ) => {
    setMenuMode(_menuMode);
    if (saveInLocalStorage && localStorage) {
      localStorage.setItem(themeMenuModeLSKey, _menuMode);
    }
  };

  useEffect(() => {
    updateMode(mode, false);
    if (apiToken) {
      getStyleColors()
        .then((data) => {
          updateStyle({ type: data?.type });

          if (data.type === "custom") {
            updateMode("custom");
          }
        })
        .catch((error) => {
          console.error(
            error?.response?.data?.error?.statusCode,
            error?.response?.data?.error?.message
          );
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeModeContext.Provider
      value={{ mode, menuMode, updateMode, updateMenuMode }}
    >
      {children}
    </ThemeModeContext.Provider>
  );
};

export { ThemeModeProvider, useThemeMode, systemMode, themeModeSwitchHelper };
